<script setup>
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pipelines: {
    type: Array,
    required: true,
  },
  currentPipelineId: {
    type: String,
    required: true,
  },
  currentView: {
    type: String,
    default: 'kanban',
  },
  searchQuery: {
    type: String,
    default: '',
  },
  selectedAgentId: {
    type: String,
    default: 'all',
  },
  selectedInboxId: {
    type: String,
    default: 'all',
  },
  slaOnlyFilter: {
    type: Boolean,
    default: false,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  totalConversationsCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits([
  'update:currentPipelineId',
  'update:currentView',
  'update:searchQuery',
  'update:selectedAgentId',
  'update:selectedInboxId',
  'update:slaOnlyFilter',
  'open-settings',
]);

const { t } = useI18n();
</script>

<template>
  <header
    class="flex flex-wrap items-center justify-between gap-3 min-h-16 w-full flex-shrink-0 border-b border-n-weak bg-n-surface-1 px-4 lg:px-6 py-2.5 shadow-sm"
  >
    <!-- Left Section: Logo / Title, Pipeline Selector & View Mode Switcher -->
    <div class="flex items-center flex-wrap gap-2.5 sm:gap-3 flex-shrink-0">
      <div class="flex items-center gap-2">
        <span
          class="flex size-8 sm:size-9 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-lucide-columns-3" class="size-4 sm:size-5" />
        </span>
        <h1
          class="text-base sm:text-lg font-extrabold text-n-slate-12 tracking-tight"
        >
          {{ t('CRM.HEADER.TITLE') }}
        </h1>
        <span
          class="flex h-5 min-w-5 items-center justify-center rounded-full bg-n-solid-2 px-1.5 text-xs font-bold text-n-slate-11 border border-n-weak"
        >
          {{ totalConversationsCount }}
        </span>
      </div>

      <!-- Pipeline Selector Dropdown -->
      <div class="relative flex items-center">
        <select
          :value="currentPipelineId"
          class="reset-base !mb-0 h-9 min-w-[140px] max-w-[210px] rounded-xl border border-n-weak bg-n-solid-1 !pl-3 !pr-8 text-xs font-semibold text-n-slate-12 shadow-sm focus:border-n-brand focus:outline-none cursor-pointer !bg-none appearance-none truncate"
          @change="$emit('update:currentPipelineId', $event.target.value)"
        >
          <option
            v-for="pipeline in pipelines"
            :key="pipeline.id"
            :value="pipeline.id"
          >
            {{ pipeline.name }}
          </option>
        </select>
        <Icon
          icon="i-lucide-chevron-down"
          class="pointer-events-none absolute right-2.5 size-4 text-n-slate-10"
        />
      </div>

      <!-- View Switcher Toggle: Kanban vs Lista -->
      <div
        class="flex items-center rounded-xl border border-n-weak bg-n-solid-1 p-0.5 shadow-2xs"
      >
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-extrabold transition-colors cursor-pointer"
          :class="
            currentView === 'kanban'
              ? 'bg-woot-500 text-[#1c1c1c] shadow-xs'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          :title="t('CRM.HEADER.KANBAN_VIEW_TITLE')"
          @click="$emit('update:currentView', 'kanban')"
        >
          <Icon icon="i-lucide-layout-grid" class="size-3.5" />
          <span>{{ t('CRM.HEADER.KANBAN_VIEW') }}</span>
        </button>

        <button
          type="button"
          class="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-extrabold transition-colors cursor-pointer"
          :class="
            currentView === 'list'
              ? 'bg-woot-500 text-[#1c1c1c] shadow-xs'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          :title="t('CRM.HEADER.LIST_VIEW_TITLE')"
          @click="$emit('update:currentView', 'list')"
        >
          <Icon icon="i-lucide-table" class="size-3.5" />
          <span>{{ t('CRM.HEADER.LIST_VIEW') }}</span>
        </button>
      </div>
    </div>

    <!-- Right Section: Filters & Quick Search Bar -->
    <div
      class="flex items-center flex-wrap gap-2 overflow-x-auto py-0.5 custom-scrollbar-h"
    >
      <!-- Search Input inside Kanban Board / List View -->
      <div class="relative flex items-center">
        <Icon
          icon="i-lucide-search"
          class="pointer-events-none absolute left-3 z-10 size-3.5 text-n-slate-10"
        />
        <input
          type="text"
          :value="searchQuery"
          :placeholder="t('CRM.HEADER.SEARCH_PLACEHOLDER')"
          class="reset-base !mb-0 h-9 w-44 sm:w-52 lg:w-56 rounded-xl border border-n-weak bg-n-solid-1 !pl-9 !pr-3 text-xs text-n-slate-12 placeholder-n-slate-10 shadow-sm focus:border-n-brand focus:outline-none"
          @input="$emit('update:searchQuery', $event.target.value)"
        />
      </div>

      <!-- Agent Filter -->
      <div class="relative flex items-center">
        <select
          :value="selectedAgentId"
          class="reset-base !mb-0 h-9 min-w-[130px] max-w-[170px] rounded-xl border border-n-weak bg-n-solid-1 !pl-3 !pr-8 text-xs font-medium text-n-slate-12 shadow-sm focus:border-n-brand focus:outline-none cursor-pointer !bg-none appearance-none truncate"
          @change="$emit('update:selectedAgentId', $event.target.value)"
        >
          <option value="all">{{ t('CRM.HEADER.ALL_AGENTS') }}</option>
          <option value="unassigned">{{ t('CRM.CARD.UNASSIGNED') }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <Icon
          icon="i-lucide-chevron-down"
          class="pointer-events-none absolute right-2.5 size-4 text-n-slate-10"
        />
      </div>

      <!-- Inbox Filter -->
      <div class="relative flex items-center">
        <select
          :value="selectedInboxId"
          class="reset-base !mb-0 h-9 min-w-[130px] max-w-[170px] rounded-xl border border-n-weak bg-n-solid-1 !pl-3 !pr-8 text-xs font-medium text-n-slate-12 shadow-sm focus:border-n-brand focus:outline-none cursor-pointer !bg-none appearance-none truncate"
          @change="$emit('update:selectedInboxId', $event.target.value)"
        >
          <option value="all">{{ t('CRM.HEADER.ALL_INBOXES') }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <Icon
          icon="i-lucide-chevron-down"
          class="pointer-events-none absolute right-2.5 size-4 text-n-slate-10"
        />
      </div>

      <!-- SLA Overdue Toggle -->
      <button
        class="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-xl border px-3 text-xs font-medium transition-colors shadow-sm cursor-pointer"
        :class="
          slaOnlyFilter
            ? 'border-red-500 bg-red-500/10 text-red-600 font-bold'
            : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12'
        "
        @click="$emit('update:slaOnlyFilter', !slaOnlyFilter)"
      >
        <Icon icon="i-lucide-alert-triangle" class="size-3.5" />
        <span>{{ t('CRM.HEADER.SLA_FILTER') }}</span>
      </button>

      <!-- Settings Button -->
      <button
        class="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-xl border border-n-weak bg-n-solid-1 px-3 text-xs font-medium text-n-slate-12 shadow-sm hover:border-n-brand hover:text-n-brand cursor-pointer"
        :title="t('CRM.HEADER.SETTINGS')"
        @click="$emit('open-settings')"
      >
        <Icon icon="i-lucide-settings" class="size-3.5" />
        <span>{{ t('CRM.HEADER.SETTINGS') }}</span>
      </button>
    </div>
  </header>
</template>

<style scoped>
.custom-scrollbar-h::-webkit-scrollbar {
  height: 4px;
}
.custom-scrollbar-h::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar-h::-webkit-scrollbar-thumb {
  background: rgba(150, 150, 150, 0.2);
  border-radius: 4px;
}
</style>
